import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sectorAlignmentApi } from '../api/sectorAlignmentApi';
import type { RealignmentJob, SectorDesk, ServiceDesk } from '../api/sectorAlignmentApi';

const KEY = 'sector-alignment';
const JOB_KEY = [KEY, 'job'];
const RUNNING = ['waiting', 'active', 'delayed'];

export const isJobRunning = (job: { state: string } | undefined): boolean => Boolean(job && RUNNING.includes(job.state));

export const jobRefetchInterval = (job: { state: string } | undefined): number | false => (isJobRunning(job) ? 1000 : false);

const validDesks = (data: unknown): ServiceDesk[] => {
  if (!Array.isArray(data)) throw new Error('Malformed service desk list');
  return data;
};

/** The selectable desks, in server order; superadmin only, like the page. A malformed payload is an error. */
export const useSectorAlignmentDesks = ({ enabled = true } = {}) =>
  useQuery({ queryKey: [KEY, 'desks'], queryFn: sectorAlignmentApi.desks, select: validDesks, enabled });

/** Name and source of a desk from the cached list (never fetches); unknown desks read as their id. */
export const useDeskInfo = () => {
  const { data } = useSectorAlignmentDesks({ enabled: false });
  return useCallback((sd: SectorDesk) => {
    const desk = data?.find((d) => d.sd === sd);
    return { name: desk?.name ?? String(sd), source: desk?.source ?? null };
  }, [data]);
};

/** Analysis is a mutation on purpose: nothing is cached, every call recomputes the listed desks (none = all). */
export const useSectorAnalysis = () =>
  useMutation({ mutationFn: (sds?: SectorDesk[]) => sectorAlignmentApi.analysis(sds), retry: false });

/** Settles on 202 and 409 alike: either way the job is worth re-reading, which restarts polling. */
export const useStartRealignment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sd: SectorDesk) => sectorAlignmentApi.realign(sd),
    retry: false,
    onSettled: () => qc.invalidateQueries({ queryKey: JOB_KEY }),
  });
};

/** Polls the job (when `enabled`); when it leaves the running states, re-runs that desk's analysis (its result carries lastRun). */
export const useSectorRealignmentJob = (rerunDesk: (sd: SectorDesk) => void, { enabled = true } = {}) => {
  const { data: job } = useQuery<RealignmentJob>({
    queryKey: JOB_KEY,
    enabled,
    queryFn: sectorAlignmentApi.job,
    refetchInterval: (q) => jobRefetchInterval(q.state.data),
  });
  const running = useRef<SectorDesk | null>(null);

  useEffect(() => {
    if (!job) return;
    if (isJobRunning(job)) {
      running.current = job.sd ?? null;
    } else if (running.current !== null) {
      const sd = running.current;
      running.current = null;
      rerunDesk(sd);
    }
  }, [job, rerunDesk]);

  return { job };
};
